import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import './IndustryShowcase.scss';

interface Industry {
  label: string;
  image: string;
  chat: {
    user: { name: string; initials: string; msg: string };
    action: string;
    ai: { msg: string };
  };
  stat1: { value: string; desc: string };
  stat2: { value: string; desc: string };
  company: string;
  ctaLabel: string;
}

const industries: Industry[] = [
  {
    label: 'Financial Services',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80&fit=crop',
    chat: {
      user: { name: 'Mike', initials: 'M', msg: "What's the status of my loan application?" },
      action: 'Retrieve application status',
      ai: { msg: "Congrats Mike, your loan has been conditionally approved! The next step is a 30 minute call with one of our loan experts. Would you like me to book that for you now?" },
    },
    stat1: { value: '84%', desc: 'automated resolution rate on chat' },
    stat2: { value: '+8 point', desc: 'increase in CSAT since launching the AI agent on chat' },
    company: 'Tilt',
    ctaLabel: 'CAEL AI for financial services',
  },
  {
    label: 'Hospitality',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=900&q=80&fit=crop',
    chat: {
      user: { name: 'James', initials: 'JA', msg: "I'd like to book a suite for 2 nights this weekend." },
      action: 'Check room availability',
      ai: { msg: "The Ocean Suite is available Friday–Sunday at $420/night. Shall I reserve it and send a confirmation to your email?" },
    },
    stat1: { value: '67%', desc: 'reduction in front desk call volume' },
    stat2: { value: '4.9★', desc: 'average guest satisfaction score after AI rollout' },
    company: 'Marriott',
    ctaLabel: 'CAEL AI for hospitality',
  },
  {
    label: 'Retail',
    image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=900&q=80&fit=crop',
    chat: {
      user: { name: 'Emma', initials: 'E', msg: 'Where is my order #48291?' },
      action: 'Track order shipment',
      ai: { msg: "Your order is out for delivery and arrives by 3 PM today. I've sent a live tracking link to your email." },
    },
    stat1: { value: '$2.7M', desc: 'estimated annual savings' },
    stat2: { value: '816,446', desc: 'engaged conversations handled' },
    company: 'IPSY',
    ctaLabel: 'CAEL AI for retail',
  },
  {
    label: 'Technology',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=900&q=80&fit=crop',
    chat: {
      user: { name: 'Marcus', initials: 'MR', msg: 'Our API is returning 503 errors on all endpoints.' },
      action: 'Check system status',
      ai: { msg: "Incident #INC-7823 opened. Engineering is actively investigating. Estimated resolution: 12 minutes." },
    },
    stat1: { value: '42%', desc: 'reduction in agent average handle time' },
    stat2: { value: '225,000+', desc: 'active customers served' },
    company: 'monday.com',
    ctaLabel: 'CAEL AI for technology',
  },
  {
    label: 'Travel',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&q=80&fit=crop',
    chat: {
      user: { name: 'Jimmy', initials: 'J', msg: 'Yes, please.' },
      action: 'Send itinerary to email',
      ai: { msg: "Done, your itinerary is in your inbox. Safe travels!" },
    },
    stat1: { value: '34%+', desc: 'higher automated resolution rate after replacing declarative chatbot with CAEL AI' },
    stat2: { value: '<1 min', desc: 'wait times for high-priority support cases' },
    company: 'Cebu Pacific',
    ctaLabel: 'CAEL AI for travel',
  },
];

// Typing animation
const TypingText: React.FC<{ text: string }> = ({ text }) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const ref = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    const type = () => {
      if (i < text.length) {
        setDisplayed(text.substring(0, i + 1));
        i++;
        ref.current = setTimeout(type, 22);
      } else {
        setDone(true);
      }
    };
    ref.current = setTimeout(type, 300);
    return () => { if (ref.current) clearTimeout(ref.current); };
  }, [text]);

  return <>{displayed}{!done && <span className="ind-cursor" />}</>;
};

const IndustryShowcase: React.FC = () => {
  const [active, setActive] = useState(0);
  const [chatKey, setChatKey] = useState(0);
  const [phase, setPhase] = useState<'dots' | 'action' | 'typing'>('dots');
  const ind = industries[active];

  const handleSelect = (i: number) => {
    if (i === active) return;
    setActive(i);
    setChatKey(k => k + 1);
  };

  useEffect(() => {
    setPhase('dots');
    const t1 = setTimeout(() => setPhase('action'), 900);
    const t2 = setTimeout(() => setPhase('typing'), 1300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [chatKey]);

  return (
    <div className="ind-root" style={{ background: '#2D2533', padding: '72px 32px', fontFamily: 'Inter, sans-serif' }}>
      <div className="ind-wrap" style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr 300px',
        alignItems: 'stretch',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '0',
      }}>

        {/* LEFT NAV */}
        <nav className="ind-nav" >
          {industries.map((item, i) => (
            <button
              key={item.label}
              onClick={() => handleSelect(i)}
              className="ind-nav__btn"
              style={{
                background: active === i ? '#FFFFFF' : '#4A3F52',
                color: active === i ? '#111111' : 'rgba(255,255,255,0.75)',
                fontWeight: active === i ? 600 : 500,
                border: 'none',
                cursor: 'pointer',
                fontFamily: "Segoe UI, 'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontSize: '13px',
                textAlign: 'left',
                borderRadius: '999px',
                width: '100%',
                transition: 'all 0.2s',
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* CENTRE — photo + chat overlay */}
        <div className="ind-visual" key={`v-${chatKey}`}>
          <img src={ind.image} alt={ind.label} className="ind-visual__img" />

          {/* Floating chat card */}
          <div className="ind-chat">
            {/* Coloured dots — top right of card */}
            <div className="ind-chat__dots-row">
              <span className="ind-chat__dot" style={{ background: '#C084FC' }} />
              <span className="ind-chat__dot" style={{ background: '#818CF8', animationDelay: '0.15s' }} />
              <span className="ind-chat__dot" style={{ background: '#60A5FA', animationDelay: '0.3s' }} />
            </div>

            {/* User message */}
            <div className="ind-chat__row">
              <div className="ind-chat__user-av">{ind.chat.user.initials}</div>
              <div className="ind-chat__col">
                <span className="ind-chat__name">{ind.chat.user.name}</span>
                <div className="ind-chat__bubble ind-chat__bubble--user">{ind.chat.user.msg}</div>
              </div>
            </div>

            {/* Action line */}
            {phase !== 'dots' && (
              <div className="ind-chat__action">{ind.chat.action}</div>
            )}

            {/* AI message */}
            <div className="ind-chat__row">
              <div className="ind-chat__ai-av">✦</div>
              <div className="ind-chat__col">
                <span className="ind-chat__name">AI Agent</span>
                <div className="ind-chat__bubble ind-chat__bubble--ai">
                  {phase === 'dots' && (
                    <span className="ind-chat__typing-dots">
                      <span /><span /><span />
                    </span>
                  )}
                  {phase === 'action' && (
                    <span className="ind-chat__typing-dots">
                      <span /><span /><span />
                    </span>
                  )}
                  {phase === 'typing' && <TypingText text={ind.chat.ai.msg} />}
                </div>
              </div>
            </div>
          </div>

          {/* CTA pill — bottom right */}
          <button className="ind-cta">
            <span>{ind.ctaLabel}</span>
            <span className="ind-cta__icon"><ArrowRight size={16} /></span>
          </button>
        </div>

        {/* RIGHT — stats */}
        <div className="ind-stats" key={`s-${active}`}>
          <div className="ind-stats__company">{ind.company}</div>
          <div className="ind-stats__item">
            <div className="ind-stats__value">{ind.stat1.value}</div>
            <div className="ind-stats__desc">{ind.stat1.desc}</div>
          </div>
          <div className="ind-stats__item">
            <div className="ind-stats__value">{ind.stat2.value}</div>
            <div className="ind-stats__desc">{ind.stat2.desc}</div>
          </div>
          {/* <a href="#" className="ind-stats__link">View case study →</a> */}
        </div>

      </div>
    </div>
  );
};

export default IndustryShowcase;